import { CacheKey } from "@/common/stores/persistingStores/app";
import {
	WhatsAppHistoryMessagesKey,
	WhatsAppMessagesKey,
	createWhatsAppMessages,
	fetchWhatsAppHistoryMessages,
	fetchWhatsAppMessages,
	uploadWhatsAppMedia,
} from "@/repository/queryFns";
import { Spinner, tokens, Tooltip } from "@fluentui/react-components";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useInfiniteQueryHook } from "../infiniteQuery";
import {
	CreateWhatsAppMessage,
	DisplayMessageType,
	ExtendedChatMessage,
	ExtendedCustomMessage,
	MessagingMethods,
	WhatsAppCCAttachmentMetadataInProgress,
	WhatsAppMessage,
	WhatsAppMessageStatus,
	WhatsAppMessageType,
} from "@/components/molecules/interactionWorkspace/content/messagingInteractionContent/types";
import {
	AttachmentMetadata,
	ChatMessage,
	CustomMessage,
	MessageProps,
	MessageRenderer,
	MessageStatus,
} from "@azure/communication-react";
import {
	CheckmarkCircleRegular,
	CircleRegular,
	ErrorCircle16Regular,
	EyeRegular,
	ImageOffRegular,
} from "@fluentui/react-icons";
import { useChatStyle } from "@/components/molecules/interactionWorkspace/content/messagingInteractionContent/style";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";

export const useWhatsAppMessaging = (
	agentName: string,
	interactionId: string,
	isHistory: boolean,
	addMessagesInChat: (newMessages: any) => void,
	handleError: (error: any, message: string) => void,
	tooltipMountNode?: HTMLElement | null
): MessagingMethods => {
	const messagesKey = !!isHistory
		? WhatsAppHistoryMessagesKey
		: WhatsAppMessagesKey;

	const [whatsAppMessagesResult, , whatsAppMessagesInView] =
		useInfiniteQueryHook<WhatsAppMessage>(
			`${messagesKey}`,
			!!isHistory ? fetchWhatsAppHistoryMessages : fetchWhatsAppMessages,
			CacheKey.WhatsApp,
			true,
			undefined,
			interactionId,
			"always"
		);

	const apiMessages = useMemo<WhatsAppMessage[]>(
		() => whatsAppMessagesResult.data?.pages.flatMap((w) => w.items) ?? [],
		[whatsAppMessagesResult, whatsAppMessagesInView]
	);

	const intl = useIntl();

	const classes = useChatStyle();

	const getSasExpiry = (sasUrl: string): Date | null => {
		try {
			const url = new URL(sasUrl);
			const expiry = url.searchParams.get("se");
			return expiry ? new Date(expiry) : null;
		} catch {
			return null;
		}
	};

	const isSasExpired = (sasUrl: string) => {
		const expiryDate = getSasExpiry(sasUrl);
		return expiryDate ? new Date() > expiryDate : false;
	};

	const getContent = (message: WhatsAppMessage) => {
		switch (message.type) {
			case WhatsAppMessageType.Text:
				return message.body;

			case WhatsAppMessageType.Image:
				if (message.mediaUrl && !isSasExpired(message.mediaUrl)) {
					return `
                        ${!!message.caption ? `<p> ${message.caption} </p>` : ""}
                        <div style="max-width: 300px;">
                            <img 
                                src="${message.mediaUrl}"
                                itemscope="png" 
                                id="${message.mediaId}" 
                                style="
                                    max-width: 100%;
                                    max-height: 200px;
									margin-bottom: -6px;
                                    object-fit: contain;
                                    border-radius: 4px;
                                    border: none;
                                    cursor: pointer;
                                "
                            />
                        </div>`;
				}
				return "";
			case WhatsAppMessageType.Document:
			case WhatsAppMessageType.Video:
			case WhatsAppMessageType.Audio:
				return message.caption !== null && message.caption != message.filename
					? message.caption
					: "";

			default:
				return intl.formatMessage({
					id: "Interaction.WhatsAppContent.MessageUnsupported",
				});
		}
	};

	const getStatus = (status: WhatsAppMessageStatus): MessageStatus => {
		switch (status) {
			case WhatsAppMessageStatus.Sending:
				return "sending" as MessageStatus;
			case WhatsAppMessageStatus.Failed:
			case WhatsAppMessageStatus.ReengagementFailed:
			case WhatsAppMessageStatus.MediaUploadFailed:
				return "failed" as MessageStatus;
			case WhatsAppMessageStatus.Read:
				return "seen" as MessageStatus;
			case WhatsAppMessageStatus.Delivered:
			default:
				return "delivered" as MessageStatus;
		}
	};

	const getStatusDescription = (messageStatus: WhatsAppMessageStatus) => {
		return intl.formatMessage({
			id: `Interaction.WhatsAppContent.MessageStatuses.${WhatsAppMessageStatus[messageStatus]}`,
		});
	};

	const getStatusIcon = (customStatus: WhatsAppMessageStatus) => {
		switch (customStatus) {
			case WhatsAppMessageStatus.Failed:
			case WhatsAppMessageStatus.ReengagementFailed:
			case WhatsAppMessageStatus.MediaUploadFailed:
				return (
					<ErrorCircle16Regular color={tokens.colorPaletteRedBorderActive} />
				);
			case WhatsAppMessageStatus.Delivered:
				return <CheckmarkCircleRegular color={tokens.colorBrandBackground} />;
			case WhatsAppMessageStatus.Read:
				return <EyeRegular color={tokens.colorBrandBackground} />;
			case WhatsAppMessageStatus.Sending:
			default:
				return <CircleRegular color={tokens.colorBrandBackground} />;
		}
	};

	const getAttachments = (message: WhatsAppMessage): AttachmentMetadata[] => {
		return message.type === WhatsAppMessageType.Text ||
			message.type === WhatsAppMessageType.Image ||
			message.type === WhatsAppMessageType.Contacts ||
			message.type === WhatsAppMessageType.Location ||
			message.type === WhatsAppMessageType.Unknown ||
			message.type === WhatsAppMessageType.Sticker
			? []
			: [
					{
						id: message.mediaId ?? crypto.randomUUID(),
						name: message.filename ?? "",
						url: message.mediaUrl!,
					},
				];
	};

	const chatMessages: (ExtendedChatMessage | CustomMessage)[] = useMemo(() => {
		const messages: (ExtendedChatMessage | CustomMessage)[] = apiMessages
			.reverse()
			.map((m, i) => {
				const nextMessage = apiMessages[i - 1];
				const isDifferentSource = nextMessage?.isFromCustomer !== m.isFromCustomer;

				const isTimeGap = nextMessage
					? dayjs(m.timestamp).diff(dayjs(nextMessage.timestamp), "minutes") > 5
					: false;
				const attached = !isDifferentSource && !isTimeGap;
				const mine = !m.isFromCustomer;

				return {
					messageType: DisplayMessageType.Chat,
					senderId: m.creatorId,
					senderDisplayName: m.senderName,
					messageId: m.id,
					content: getContent(m),
					createdOn: new Date(m.timestamp),
					mine: mine,
					attached: attached,
					contentType: "html",
					status: getStatus(m.status),
					statusElement: mine ? (
						<Tooltip
							relationship="label"
							content={getStatusDescription(m.status)}
							mountNode={tooltipMountNode}
						>
							<div className={classes.statusIcon}>
								{getStatusIcon(m.status)}
							</div>
						</Tooltip>
					) : undefined,
					showStatus: true,
					attachments: getAttachments(m),
					metadata: {
						mediaId: `${m.mediaId}`,
					},
				};
			});

		messages.unshift({
			messageType: "custom",
			content: "spinner",
			messageId: "loading",
			createdOn: new Date(),
		});

		return messages;
	}, [apiMessages]);

	const isOnlyEmojis = (text: string) => {
		const emojiRegex = /^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)+$/u;
		return emojiRegex.test(text);
	};

	const isChatMessage = (
		msg: ChatMessage | CustomMessage
	): msg is ChatMessage => {
		return (msg as ChatMessage).senderId !== undefined;
	};

	const applyMessageStyles = (message: WhatsAppMessage) => {
		if (message.type === WhatsAppMessageType.Text) {
			if (isOnlyEmojis(message.body)) {
				return {
					fontSize: "3em",
					backgroundColor: "transparent",
					display: "flex",
					flexDirection: "column",
					gap: "0.3em",
					margin: "0px 0px 10px -50px",
				};
			}

			return {};
		}

		return {};
	};

	const onRenderMessage = (
		messageProps: MessageProps,
		defaultOnRender?: MessageRenderer
	): JSX.Element => {
		const { message } = messageProps as unknown as {
			message: ExtendedChatMessage | ExtendedCustomMessage;
		};

		if (!isChatMessage(message) && !!whatsAppMessagesResult.hasNextPage) {
			return (
				<Spinner
					ref={whatsAppMessagesInView.ref}
					size="tiny"
					label={intl.formatMessage({ id: "Common.Loading" })}
				/>
			);
		}

		const chatMessage = message as ChatMessage;
		const isMine = chatMessage.senderId !== null;
		const senderDisplayName = chatMessage.senderDisplayName;

		const msg = apiMessages.find((m) => m.id === chatMessage.messageId);

		if (msg) {
			messageProps.messageContainerStyle = applyMessageStyles(msg);

			if (
				msg.type === WhatsAppMessageType.Image &&
				(!msg.mediaUrl || isSasExpired(msg.mediaUrl))
			) {
				return (
					<>
						<div
							style={{
								margin: "6px 2px 4px 2px",
								padding: "60px 0px 60px 70px",
								border: "1px solid lightgray",
								maxWidth: "180px",
								maxHeight: "180px",
								borderRadius: "4px",
								float: isMine ? "right" : "left",
								width: "inherit",
							}}
						>
							<ImageOffRegular fontSize={40} />
						</div>
					</>
				);
			}

			if (
				(msg.type === WhatsAppMessageType.Image ||
					msg.type === WhatsAppMessageType.Audio ||
					msg.type === WhatsAppMessageType.Video ||
					msg.type === WhatsAppMessageType.Document) &&
				!msg.mediaUrl
			) {
				return <></>;
			}
		}

		return (
			<div
				title={isMine ? senderDisplayName : ""}
				className={isMine ? classes.sentMessage : ""}
			>
				{defaultOnRender ? defaultOnRender(messageProps) : <></>}
				{isMine && message.showStatus && message.statusElement}
				{isMine && !message.showStatus && (
					<span style={{ width: "14px" }}></span>
				)}
			</div>
		);
	};

	const uploadMedia = async (
		messages: WhatsAppMessage[],
		attachments?: WhatsAppCCAttachmentMetadataInProgress[]
	) => {
		if (!attachments?.length) {
			return;
		}

		await Promise.all(
			messages.map((message, index) => {
				if (attachments[index]) {
					const attachment = attachments[index];
					if (!attachment) {
						return;
					}

					uploadWhatsAppMedia(interactionId ?? "", message.id ?? "", {
						file: attachment.file,
						filename: attachment.name,
						type: attachment.type,
						caption: message.caption,
					}).catch((ex) => {
						handleError(ex, "Error while uploading WhatsApp media");
					});
				}
			})
		);
	};

	const createNewMessages = (
		content: string,
		status: WhatsAppMessageStatus,
		attachments?: WhatsAppCCAttachmentMetadataInProgress[]
	): WhatsAppMessage[] => {
		if (!content && attachments?.length == 0) {
			return [];
		}

		let newMessages: WhatsAppMessage[] = [];

		if (attachments?.length == 0) {
			newMessages.push({
				creatorId: Lockr.get(StorageKeys.UserId),
				senderName: agentName,
				body: content,
				timestamp: new Date().toISOString(),
				status: status,
				id: crypto.randomUUID(),
				type: WhatsAppMessageType.Text,
				isFromCustomer: false,
			});
		} else if (attachments && attachments.length > 0) {
			newMessages =
				attachments?.map((a: WhatsAppCCAttachmentMetadataInProgress) => ({
					creatorId: Lockr.get(StorageKeys.UserId),
					senderName: agentName,
					body: content,
					timestamp: new Date().toISOString(),
					status: status,
					id: crypto.randomUUID(),
					type: a.type,
					filename: a.name,
					mediaUrl: a.url,
					isFromCustomer: false,
				})) || [];

			if (newMessages[0]) {
				newMessages[0].caption = content;
			}
		}
		addMessagesInChat(newMessages);

		return newMessages;
	};

	const sendMessages = async (
		content: string,
		attachments?: WhatsAppCCAttachmentMetadataInProgress[]
	) => {
		const lastCustomerMessage = [...apiMessages] // Avoid mutating the original array
			.reverse()
			.find((w) => w.creatorId === null);

		const isCustomerWindowReplyExceeded =
			dayjs().diff(dayjs(lastCustomerMessage?.timestamp), "hours") > 24;
		const allNewMessagesStatus = isCustomerWindowReplyExceeded
			? WhatsAppMessageStatus.Failed
			: WhatsAppMessageStatus.Sending;

		const messages = createNewMessages(
			content,
			allNewMessagesStatus,
			attachments
		);

		const messagesToBeCreated: CreateWhatsAppMessage[] = messages.map(
			({ id, body, status, type }) => ({
				id,
				input: body,
				status,
				type,
			})
		);

		createWhatsAppMessages(interactionId ?? "", messagesToBeCreated)
			.then(() => uploadMedia(messages, attachments))
			.catch((ex: any) =>
				handleError(ex, "Error while creating WhatsApp messages")
			);
	};

	return {
		chatMessages,
		apiMessages,
		onRenderMessage,
		sendMessages,
	};
};
